/** 按控件类型确定数字输入框数量；controlType 为接口配置的控件类型。 */
export function getMeasureInputCount(controlType) {
  return { number: 1, range: 2, multiNumber: 3 }[controlType] || 0;
}

/** 数字框之间的分隔符；宽高 range 显示 x，有调节器 range 不显示分隔符，其余 range 仍用 ~。 */
export function getMeasureSeparator(config = {}) {
  if (config.controlType === "range") {
    const name = String(config.name || "").trim();
    if (name === "宽cm×高cm") return "x";
    if (name === "有调节器cm") return "";
    return "~";
  }
  return "/";
}

/** 有调节器的 range 第二个输入框使用「调节器」前缀。 */
export function getMeasureInputPrefix(config = {}, position) {
  return config.controlType === "range"
    && String(config.name || "").trim() === "有调节器cm"
    && Number(position) === 2
    ? "调节器"
    : "";
}

/** 数字、范围和多值输入框的占位单位，无 unit 时不展示。 */
export function getMeasureInputPlaceholder(config = {}) {
  if (!getMeasureInputCount(config.controlType)) return "";
  return String(config.unit || "").trim();
}

/** 拆分后端约定的逗号分隔数值；value 为测量字符串，保留未填位置供编辑和校验。 */
export function splitMeasureValue(value) {
  return String(value == null ? "" : value).split(",");
}

/** 数字输入最多保留两位小数；value 为当前输入文本，允许中间态如 12. */
export function limitMeasureDecimal(value) {
  const text = String(value == null ? "" : value).replace(/[^\d.]/g, "");
  if (!text) return "";
  const firstDot = text.indexOf(".");
  if (firstDot < 0) return text;
  const integer = text.slice(0, firstDot);
  const decimal = text.slice(firstDot + 1).replace(/\./g, "").slice(0, 2);
  return integer + "." + decimal;
}

/** 将配置和已保存结果合并成独立 DTO；config 为配置，saved 为对应的回显记录。 */
export function createMeasureResult(config, saved = {}) {
  // 仅复制接口支持的字段，避免修改父级数据或把配置选项全部提交。
  const result = {
    code: String(config.key),
    name: config.name,
    controlType: config.controlType,
    required: Boolean(config.required),
    // 原样保留测量项的套装部件，随单次提交和批量复制传给后端。
    suiteComponents: config.suiteComponents,
    value: saved.value == null ? "" : String(saved.value),
    options: [],
  };
  if (config.controlType === "select") {
    // 已保存的选中分支，也兼容通过 options 返回选中项的记录。
    const savedOptions = saved.options || [];
    result.value = result.value || String(savedOptions[0]?.code || "");
    // 根据编码匹配选中项，确保配置顺序改变后仍能正确回显。
    const selected = (config.options || []).find((option) => String(option.key) === result.value);
    if (selected) {
      result.options = [createMeasureResult(selected,
        savedOptions.find((option) => String(option.code) === result.value))];
    }
  } else if (config.controlType === "none") {
    result.value = "";
  }
  return result;
}

/** 按当前分类生成表单结果；items 为测量配置，values 为父级保存的 DTO 列表。 */
export function createMeasureResults(items, values) {
  // 套装可能出现相同编码，按出现顺序消费回显，避免后一个部件读到前一个的值。
  const unused = (values || []).slice();
  return items.map((item) => {
    const found = unused.findIndex((value) => value && String(value.code) === String(item.key));
    const saved = found >= 0 ? unused.splice(found, 1)[0] : undefined;
    return createMeasureResult(item, saved);
  });
}

/** 把分类说明里的换行标记转成真实换行，兼容后端返回的 /n、\\n 和真实换行。 */
export function formatCategoryTips(tips) {
  return String(tips == null ? "" : tips)
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\/n/g, "\n")
    .replace(/\r\n|\r/g, "\n")
    .trim();
}

/** 按套装部件拆成带标题的板块；title 为 suiteComponents，indexes 为原测量项位置。 */
export function groupMeasureItems(items) {
  const groups = [];
  const indexByTitle = new Map();
  (items || []).forEach((item, index) => {
    const title = String(item.suiteComponents || "").trim();
    let groupIndex = indexByTitle.get(title);
    if (groupIndex == null) {
      groupIndex = groups.length;
      indexByTitle.set(title, groupIndex);
      groups.push({ title, indexes: [] });
    }
    groups[groupIndex].indexes.push(index);
  });
  return groups;
}

/** 判断结果是否已填写；选中 none 分支的下拉项也属于有效测量结果。 */
export function hasMeasureValue(result) {
  return result.controlType !== "none" && String(result.value || "").trim() !== "";
}

/** 校验单项测量；config 为配置，result 为结果，forceRequired 表示已选分支需要填完整。 */
export function getMeasureError(config, result, forceRequired = false) {
  if (config.controlType === "none") return "";
  if (!hasMeasureValue(result)) {
    return config.required || forceRequired ? `请填写${config.name}` : "";
  }
  if (config.controlType === "select") {
    // 当前选中配置，用于递归校验其输入控件。
    const selected = (config.options || []).find((option) => String(option.key) === result.value);
    if (!selected) return `请重新选择${config.name}`;
    return getMeasureError(selected, result.options[0], true);
  }
  // 数字控件必须完整填写规定数量，避免只提交范围的一端。
  const values = splitMeasureValue(result.value);
  if (values.length !== getMeasureInputCount(config.controlType) || values.some((value) => !value.trim())) {
    return `请完整填写${config.name}`;
  }
  if (values.some((value) => !Number.isFinite(Number(value)) || Number(value) <= 0)) {
    return `${config.name}必须是大于0的数字`;
  }
  if (values.some((value) => (String(value).split(".")[1] || "").length > 2)) {
    return `${config.name}最多保留2位小数`;
  }
  return "";
}
